import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Shield, Check, Zap, Building2, Crown, ArrowRight, Loader2, Star, X
} from 'lucide-react';
import { toast } from 'sonner';

const PLAN_ICONS = { starter: Zap, professional: Star, enterprise: Building2, whitelabel: Crown };
const PLAN_ACCENTS = {
  starter: 'border-gray-200',
  professional: 'border-[#D4A843] ring-2 ring-[#D4A843]/20',
  enterprise: 'border-[#D4A843]',
  whitelabel: 'border-gray-800 bg-[#0f172a] text-white',
};

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/pricing/plans`).then(r => r.json()).then(d => setPlans(d.plans || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = async () => {
    if (!form.email) { toast.error('Email required'); return; }
    setSubmitting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/pricing/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, plan_id: selectedPlan, billing }),
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success('Subscription request received! We\'ll be in touch within 24 hours.');
      }
    } catch { toast.error('Failed — try again'); } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      {/* Header */}
      <nav className="border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#D4A843] rounded-lg flex items-center justify-center"><Shield className="w-3.5 h-3.5 text-white" /></div>
            <span className="font-heading text-base font-bold text-gray-900">TechOps</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs text-gray-500 hover:text-gray-900">Home</Link>
            <Link to="/login" className="text-xs text-gray-500 hover:text-gray-900">Sign In</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-20 text-center px-6">
        <p className="uppercase text-xs tracking-[0.2em] font-medium text-[#D4A843] mb-3">Pricing</p>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-4">
          Plans that scale with your business
        </h1>
        <p className="text-base text-gray-500 max-w-xl mx-auto mb-8">
          From startups to enterprise. IT support, cybersecurity, and automation — all in one platform.
        </p>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-3 mb-12" data-testid="billing-toggle">
          <span className={`text-sm ${billing === 'monthly' ? 'text-gray-900 font-bold' : 'text-gray-400'}`}>Monthly</span>
          <button onClick={() => setBilling(b => b === 'monthly' ? 'annual' : 'monthly')} className={`relative w-14 h-7 rounded-full transition-colors ${billing === 'annual' ? 'bg-[#D4A843]' : 'bg-gray-300'}`}>
            <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${billing === 'annual' ? 'translate-x-7' : 'translate-x-0.5'}`} />
          </button>
          <span className={`text-sm ${billing === 'annual' ? 'text-gray-900 font-bold' : 'text-gray-400'}`}>Annual</span>
          {billing === 'annual' && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">Save 20%</span>}
        </div>
      </section>

      {/* Plans Grid */}
      <section className="px-6 pb-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="plans-grid">
          {plans.map(plan => {
            const Icon = PLAN_ICONS[plan.id] || Zap;
            const price = billing === 'annual' ? plan.price_annual : plan.price_monthly;
            const isWhiteLabel = plan.id === 'whitelabel';

            return (
              <div key={plan.id} className={`relative border-2 rounded-2xl p-6 flex flex-col ${PLAN_ACCENTS[plan.id]} ${plan.popular ? '' : ''}`} data-testid={`plan-${plan.id}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4A843] text-[#0A0A0A] font-semibold text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">Most Popular</div>
                )}
                <Icon className={`w-8 h-8 mb-4 ${isWhiteLabel ? 'text-[#D4A843]' : 'text-[#D4A843]'}`} />
                <h3 className={`font-heading text-xl font-bold mb-1 ${isWhiteLabel ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                <p className={`text-xs mb-4 ${isWhiteLabel ? 'text-gray-400' : 'text-gray-500'}`}>{plan.tagline}</p>

                <div className="mb-6">
                  <span className={`text-4xl font-bold ${isWhiteLabel ? 'text-white' : 'text-gray-900'}`}>${price}</span>
                  <span className={`text-sm ${isWhiteLabel ? 'text-gray-400' : 'text-gray-500'}`}>/month</span>
                  {billing === 'annual' && <p className={`text-[10px] mt-0.5 ${isWhiteLabel ? 'text-gray-500' : 'text-gray-400'}`}>billed annually (${price * 12}/yr)</p>}
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={`item-${i}`} className={`flex items-start gap-2 text-xs ${isWhiteLabel ? 'text-gray-300' : 'text-gray-600'}`}>
                      <Check className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${isWhiteLabel ? 'text-[#D4A843]' : plan.popular ? 'text-[#D4A843]' : 'text-emerald-500'}`} />
                      {f}
                    </li>
                  ))}
                </ul>

                <Button onClick={() => setSelectedPlan(plan.id)} className={`w-full rounded-lg text-sm font-bold h-11 ${plan.popular ? 'bg-[#D4A843] hover:bg-[#001f7a] text-[#0A0A0A] font-semibold' : isWhiteLabel ? 'bg-[#D4A843] hover:bg-[#c49a3a] text-[#0f172a]' : 'bg-gray-900 hover:bg-gray-800 text-white'}`} data-testid={`select-${plan.id}`}>
                  {isWhiteLabel ? 'Contact Sales' : 'Get Started'} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Trust Bar */}
      <section className="border-t border-gray-100 bg-gray-50 py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Trusted by businesses worldwide</p>
          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
            <span>99.9% Uptime SLA</span>
            <span>HIPAA Compliant</span>
            <span>SOC 2 Ready</span>
            <span>24/7 Support</span>
            <span>30-Day Money Back</span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          {[
            { q: 'Can I switch plans later?', a: 'Yes. Upgrade or downgrade at any time. Changes take effect on your next billing cycle.' },
            { q: 'Is there a free trial?', a: 'We offer a free IT Security Assessment. Contact us for a demo of the full platform.' },
            { q: 'What payment methods do you accept?', a: 'Credit cards, ACH, wire transfer. Enterprise and White-Label plans support custom invoicing.' },
            { q: 'What does White-Label include?', a: 'Full rebrand of the TechOps platform — your logo, colors, domain. Resell to your own clients as your own product.' },
            { q: 'Do you offer discounts for nonprofits?', a: 'Yes. Contact us for special nonprofit pricing.' },
          ].map((faq, i) => (
            <div key={`item-${i}`} className="border-b border-gray-100 py-4">
              <h3 className="text-sm font-bold text-gray-900 mb-1">{faq.q}</h3>
              <p className="text-xs text-gray-500">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 bg-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#D4A843] rounded flex items-center justify-center"><Shield className="w-3 h-3 text-white" /></div>
            <span className="font-heading text-sm font-bold text-gray-900">TechOps</span>
          </div>
          <div className="flex gap-6 text-xs text-gray-500">
            <Link to="/policies" className="hover:text-gray-900">Privacy</Link>
            <Link to="/policies" className="hover:text-gray-900">Terms</Link>
          </div>
          <p className="text-xs text-gray-400">2026 EIGOO Inc | <a href="https://eigooinc.net" className="text-[#D4A843]">eigooinc.net</a> | Powered by <a href="https://app.emergent.sh/?via=Eigoo" target="_blank" rel="noopener noreferrer" className="text-[#D4A843] hover:underline">Emergent</a> | <a href="https://www.skool.com/signup?ref=e3e2967f8a3846bb87a53db8b592c54e" target="_blank" rel="noopener noreferrer" className="text-[#D4A843] hover:underline">Join our Skool</a></p>
        </div>
      </footer>

      {/* Subscription Modal */}
      {selectedPlan && !submitted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fade-in" data-testid="subscribe-modal">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl relative w-full">
            <button onClick={() => setSelectedPlan(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Get Started with {plans.find(p => p.id === selectedPlan)?.name}</h2>
            <p className="text-xs text-gray-500 mb-5">${billing === 'annual' ? plans.find(p => p.id === selectedPlan)?.price_annual : plans.find(p => p.id === selectedPlan)?.price_monthly}/month ({billing})</p>
            <div className="space-y-3">
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Full Name" className="rounded-lg" data-testid="sub-name" />
              <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="Work Email *" type="email" required className="rounded-lg" data-testid="sub-email" />
              <Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} placeholder="Company Name" className="rounded-lg" data-testid="sub-company" />
              <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Phone" className="rounded-lg" />
              <Button onClick={subscribe} disabled={submitting} className="w-full bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg h-11 font-bold" data-testid="submit-subscription">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Start My Subscription
              </Button>
            </div>
            <p className="text-[9px] text-gray-400 text-center mt-3">Our team will contact you within 24 hours to complete setup.</p>
          </div>
        </div>
      )}

      {/* Success State */}
      {submitted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fade-in" data-testid="subscribe-success">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-emerald-600" /></div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">You're All Set!</h2>
            <p className="text-sm text-gray-500 mb-6">We've received your subscription request. Check your email for confirmation — our team will reach out within 24 hours.</p>
            <Button onClick={() => { setSubmitted(false); setSelectedPlan(null); }} className="bg-[#D4A843] text-[#0A0A0A] font-semibold rounded-lg">Back to Plans</Button>
          </div>
        </div>
      )}
    </div>
  );
}
